import { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { 
    Plus, 
    Copy, 
    Check, 
    Trash2, 
    AlertCircle, 
    Clock, 
    Shield, 
    KeyRound, 
    Terminal, 
    Loader2,
    CheckCircle2,
    Ban
} from 'lucide-react';
import { getApiKeys, createApiKey, deleteApiKey } from '@/api/api-keys/api_keys.api';
import type { ApiKey } from '@/api/api-keys/api_keys.types';
import { format } from 'date-fns';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

export function APISettings() {
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [keyToDelete, setKeyToDelete] = useState<{ id: string; description: string } | null>(null);
    const [newKeyDescription, setNewKeyDescription] = useState('');
    const [newApiKey, setNewApiKey] = useState<string | null>(null);
    const [copiedStates, setCopiedStates] = useState<{ [key: string]: boolean }>({});
    const { error, success } = useEnhancedToast();

    // Create refs for toast functions
    const errorRef = useRef(error);
    const successRef = useRef(success);

    // Update refs when toast functions change
    useEffect(() => {
        errorRef.current = error;
        successRef.current = success;
    }, [error, success]);

    useEffect(() => {
        fetchApiKeys();
    }, []);

    const fetchApiKeys = async () => {
        try {
            setIsLoading(true);
            const response = await getApiKeys();
            if (response.error) throw new Error(response.error);
            setApiKeys(response.data || []);
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Failed to Load API Keys",
                description: err instanceof Error ? err.message : "Could not fetch API keys",
                duration: 4000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateKey = async () => {
        if (!newKeyDescription.trim()) {
            errorRef.current({
                icon: Ban,
                title: "Missing Description",
                description: "Please provide a description for the API key",
                duration: 4000,
            });
            return;
        }

        try {
            setIsCreating(true);
            const response = await createApiKey({ description: newKeyDescription });
            if (response.error) throw new Error(response.error);
            
            setNewApiKey(response.data?.api_key || null);
            await fetchApiKeys();
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Creation Failed",
                description: err instanceof Error ? err.message : "Could not create API key",
                duration: 4000,
            });
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeleteKey = async (keyId: string) => {
        try {
            setIsDeleting(true);
            const response = await deleteApiKey(keyId);
            if (response.error) throw new Error(response.error);
            
            await fetchApiKeys();
            setShowDeleteDialog(false);
            setKeyToDelete(null);
            
            successRef.current({
                icon: CheckCircle2,
                title: "API Key Deleted",
                description: "The API key has been deleted successfully",
                duration: 4000,
            });
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Delete Failed",
                description: err instanceof Error ? err.message : "Could not delete API key",
                duration: 4000,
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCopy = (text: string, key: string) => {
        navigator.clipboard.writeText(text);
        setCopiedStates({ ...copiedStates, [key]: true });
        setTimeout(() => {
            setCopiedStates({ ...copiedStates, [key]: false });
        }, 2000);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'bg-green-500/10 text-green-500 hover:bg-green-500/20';
            case 'expired':
                return 'bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20';
            case 'revoked':
                return 'bg-red-500/10 text-red-500 hover:bg-red-500/20';
            default:
                return 'bg-gray-500/10 text-gray-500 hover:bg-gray-500/20';
        }
    };

    const handleDialogChange = (open: boolean) => {
        setShowCreateDialog(open);
        if (!open) {
            setNewApiKey(null);
            setNewKeyDescription('');
        }
    };

    return (
        <div className="space-y-6">
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <KeyRound className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <CardTitle>API Keys</CardTitle>
                                <CardDescription>Manage your API keys for external integrations</CardDescription>
                            </div>
                        </div>
                        {(!isLoading && apiKeys.length > 0) && (
                            <Dialog open={showCreateDialog} onOpenChange={handleDialogChange}>
                                <DialogTrigger asChild>
                                    <Button className="gap-2" size="sm">
                                        <Plus className="h-4 w-4" />
                                        Create API Key
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-md p-0">
                                    <DialogHeader className="border-b bg-muted/20 px-6 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                                {newApiKey ? (
                                                    <CheckCircle2 className="h-4 w-4 text-primary" />
                                                ) : (
                                                    <KeyRound className="h-4 w-4 text-primary" />
                                                )}
                                            </div>
                                            <div className="flex-1 space-y-1">
                                                <DialogTitle className="text-base font-semibold">
                                                    {newApiKey ? "API Key Generated" : "Create New API Key"}
                                                </DialogTitle>
                                                <DialogDescription className="text-xs text-muted-foreground">
                                                    {newApiKey 
                                                        ? "Copy your key now - it won't be shown again" 
                                                        : "Create a new API key for external integrations"
                                                    }
                                                </DialogDescription>
                                            </div>
                                        </div>
                                    </DialogHeader>
                                    
                                    <div className="px-6 py-4">
                                        {newApiKey ? (
                                            <div className="space-y-6">
                                                <div className="p-4 bg-amber-50/50 rounded-lg border border-amber-200 dark:border-amber-800/50 dark:bg-amber-950/20">
                                                    <div className="flex items-start gap-3">
                                                        <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5 shrink-0" />
                                                        <div>
                                                            <p className="font-medium text-sm text-amber-900 dark:text-amber-100">Important Security Notice</p>
                                                            <p className="text-xs text-amber-800/90 dark:text-amber-200/80">
                                                                This API key will only be shown once and cannot be recovered later. 
                                                                Please copy it now and store it in a secure location.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="generated-key" className="flex items-center gap-2 text-sm font-medium">
                                                        <KeyRound className="h-4 w-4 text-primary" />
                                                        Your New API Key
                                                    </Label>
                                                    <div className="flex items-center gap-2">
                                                        <code className="flex-1 p-3 rounded-md bg-muted font-mono text-sm break-all border">
                                                            {newApiKey}
                                                        </code>
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            className="h-9 w-9"
                                                            onClick={() => handleCopy(newApiKey, 'new')}
                                                        >
                                                            {copiedStates['new'] ? (
                                                                <Check className="h-4 w-4 text-green-500" />
                                                            ) : (
                                                                <Copy className="h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="description" className="flex items-center gap-2 text-sm font-medium">
                                                        <Terminal className="h-4 w-4 text-primary" />
                                                        Description
                                                    </Label>
                                                    <Input
                                                        id="description"
                                                        placeholder="e.g., Production API Key"
                                                        value={newKeyDescription}
                                                        onChange={(e) => setNewKeyDescription(e.target.value)}
                                                        className="h-9"
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Add a description to help you identify this API key later.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <DialogFooter className="border-t bg-muted/20 px-6 py-3">
                                        {newApiKey ? (
                                            <Button 
                                                onClick={() => handleDialogChange(false)}
                                                className="w-full sm:w-auto gap-2"
                                                size="sm"
                                            >
                                                <CheckCircle2 className="h-4 w-4" />
                                                Done
                                            </Button>
                                        ) : (
                                            <Button 
                                                onClick={handleCreateKey} 
                                                disabled={isCreating}
                                                className="w-full sm:w-auto gap-2"
                                                size="sm"
                                            >
                                                {isCreating ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <KeyRound className="h-4 w-4" />
                                                )}
                                                Generate API Key
                                            </Button>
                                        )}
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="p-6">
                    {isLoading ? (
                        <div className="space-y-4">
                            {[1, 2].map((i) => (
                                <div key={i} className="flex items-center justify-between p-4 rounded-lg border">
                                    <div className="space-y-3">
                                        <Skeleton className="h-4 w-[250px]" />
                                        <Skeleton className="h-4 w-[200px]" />
                                    </div>
                                    <Skeleton className="h-8 w-[100px]" />
                                </div>
                            ))}
                        </div>
                    ) : apiKeys.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 space-y-4 text-center">
                            <div className="p-4 rounded-full bg-primary/10">
                                <Terminal className="h-8 w-8 text-primary" />
                            </div>
                            <div className="space-y-2">
                                <h3 className="font-semibold text-base">No API Keys</h3>
                                <p className="text-sm text-muted-foreground max-w-sm">
                                    You haven't created any API keys yet. Create one to start integrating with external services.
                                </p>
                            </div>
                            <Dialog open={showCreateDialog} onOpenChange={handleDialogChange}>
                                <DialogTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="gap-2"
                                        size="sm"
                                    >
                                        <Plus className="h-4 w-4" />
                                        Create API Key
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-md p-0">
                                    <DialogHeader className="border-b bg-muted/20 px-6 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                                {newApiKey ? (
                                                    <CheckCircle2 className="h-4 w-4 text-primary" />
                                                ) : (
                                                    <KeyRound className="h-4 w-4 text-primary" />
                                                )}
                                            </div>
                                            <div className="flex-1 space-y-1">
                                                <DialogTitle className="text-base font-semibold">
                                                    {newApiKey ? "API Key Generated" : "Create New API Key"}
                                                </DialogTitle>
                                                <DialogDescription className="text-xs text-muted-foreground">
                                                    {newApiKey 
                                                        ? "Copy your key now - it won't be shown again" 
                                                        : "Create a new API key for external integrations"
                                                    }
                                                </DialogDescription>
                                            </div>
                                        </div>
                                    </DialogHeader>
                                    
                                    <div className="px-6 py-4">
                                        {newApiKey ? (
                                            <div className="space-y-6">
                                                <div className="p-4 bg-amber-50/50 rounded-lg border border-amber-200 dark:border-amber-800/50 dark:bg-amber-950/20">
                                                    <div className="flex items-start gap-3">
                                                        <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5 shrink-0" />
                                                        <div>
                                                            <p className="font-medium text-sm text-amber-900 dark:text-amber-100">Important Security Notice</p>
                                                            <p className="text-xs text-amber-800/90 dark:text-amber-200/80">
                                                                This API key will only be shown once and cannot be recovered later. 
                                                                Please copy it now and store it in a secure location.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="generated-key" className="flex items-center gap-2 text-sm font-medium">
                                                        <KeyRound className="h-4 w-4 text-primary" />
                                                        Your New API Key
                                                    </Label>
                                                    <div className="flex items-center gap-2">
                                                        <code className="flex-1 p-3 rounded-md bg-muted font-mono text-sm break-all border">
                                                            {newApiKey}
                                                        </code>
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            className="h-9 w-9"
                                                            onClick={() => handleCopy(newApiKey, 'new')}
                                                        >
                                                            {copiedStates['new'] ? (
                                                                <Check className="h-4 w-4 text-green-500" />
                                                            ) : (
                                                                <Copy className="h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="description" className="flex items-center gap-2 text-sm font-medium">
                                                        <Terminal className="h-4 w-4 text-primary" />
                                                        Description
                                                    </Label>
                                                    <Input
                                                        id="description"
                                                        placeholder="e.g., Production API Key"
                                                        value={newKeyDescription}
                                                        onChange={(e) => setNewKeyDescription(e.target.value)}
                                                        className="h-9"
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Add a description to help you identify this API key later.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <DialogFooter className="border-t bg-muted/20 px-6 py-3">
                                        {newApiKey ? (
                                            <Button 
                                                onClick={() => handleDialogChange(false)}
                                                className="w-full sm:w-auto gap-2"
                                                size="sm"
                                            >
                                                <CheckCircle2 className="h-4 w-4" />
                                                Done
                                            </Button>
                                        ) : (
                                            <Button 
                                                onClick={handleCreateKey} 
                                                disabled={isCreating}
                                                className="w-full sm:w-auto gap-2"
                                                size="sm"
                                            >
                                                {isCreating ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <KeyRound className="h-4 w-4" />
                                                )}
                                                Generate API Key
                                            </Button>
                                        )}
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </div>
                    ) : (
                        <div className="rounded-lg border">
                            <Table>
                                <TableHeader className="bg-muted/50">
                                    <TableRow>
                                        <TableHead>Description</TableHead>
                                        <TableHead>Key</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Created</TableHead>
                                        <TableHead>Expires</TableHead>
                                        <TableHead className="w-[100px]"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {apiKeys.map((key) => (
                                        <TableRow key={key._id}>
                                            <TableCell className="font-medium">
                                                {key.description}
                                            </TableCell>
                                            <TableCell>
                                                <code className="px-2 py-1 rounded bg-muted font-mono text-sm">
                                                    {key.masked_key}
                                                </code>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="secondary" className={getStatusColor(key.status)}>
                                                    {key.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <TooltipProvider delayDuration={200}>
                                                    <Tooltip>
                                                        <TooltipTrigger className="flex items-center gap-2 text-sm text-muted-foreground">
                                                            <Clock className="h-4 w-4" />
                                                            {format(new Date(key.created_at), 'MMM d, yyyy')}
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            {format(new Date(key.created_at), 'PPpp')}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </TableCell>
                                            <TableCell>
                                                <TooltipProvider delayDuration={200}>
                                                    <Tooltip>
                                                        <TooltipTrigger className="flex items-center gap-2 text-sm text-muted-foreground">
                                                            <Shield className="h-4 w-4" />
                                                            {format(new Date(key.expires_at), 'MMM d, yyyy')}
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            {format(new Date(key.expires_at), 'PPpp')}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                                    onClick={() => {
                                                        setKeyToDelete({
                                                            id: key._id,
                                                            description: key.description
                                                        });
                                                        setShowDeleteDialog(true);
                                                    }}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent className="sm:max-w-md p-0">
                    <DialogHeader className="border-b bg-muted/20 px-6 py-3">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-destructive/10">
                                <Trash2 className="h-4 w-4 text-destructive" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <DialogTitle className="text-base font-semibold">Delete API Key</DialogTitle>
                                <DialogDescription className="text-xs text-muted-foreground">
                                    Permanently delete this API key and revoke all access
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>
                    
                    <div className="px-6 py-4 space-y-4">
                        {keyToDelete && (
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2 text-sm font-medium">
                                    <Terminal className="h-4 w-4 text-primary" />
                                    API Key Description
                                </Label>
                                <div className="p-3 rounded-md bg-muted border">
                                    <p className="text-sm font-medium">{keyToDelete.description}</p>
                                </div>
                            </div>
                        )}

                        <div className="p-4 border rounded-lg bg-destructive/5 dark:bg-destructive/10">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                                <div>
                                    <p className="font-medium text-sm text-destructive">Warning</p>
                                    <p className="text-xs text-destructive/90">
                                        Deleting this API key will immediately revoke access for any services using it. 
                                        This action cannot be undone.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="border-t bg-muted/20 px-6 py-3">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setShowDeleteDialog(false);
                                setKeyToDelete(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => keyToDelete && handleDeleteKey(keyToDelete.id)}
                            disabled={isDeleting}
                            className="gap-2"
                        >
                            {isDeleting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Trash2 className="h-4 w-4" />
                            )}
                            Delete API Key
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
